import {isFeatureEnabled} from '@github-ui/feature-flags'

export interface UiReadinessHandoff {
  readonly version: 1
  readonly clientOpenId: string
  readonly clockId: string
  readonly observedAtMs: number
  readonly monotonicMs: number
  readonly surface: string
  readonly entryKind: string
  readonly repositoryScope: string
}

interface UiReadinessHandoffObserver {
  opened(source: string): UiReadinessHandoff | undefined
  claimed(seed: UiReadinessHandoff): void
  failed(seed: UiReadinessHandoff): void
  reportError(): void
}

let observer: UiReadinessHandoffObserver | undefined

function reportObserverError() {
  try {
    if (observer) observer.reportError()
    else {
      // eslint-disable-next-line no-console
      console.error('Unable to observe Copilot readiness handoff telemetry')
    }
  } catch {
    // eslint-disable-next-line no-console
    console.error('Unable to report Copilot readiness handoff telemetry error')
  }
}

function readinessEnabled(): boolean {
  try {
    return isFeatureEnabled('copilot_ui_readiness_telemetry')
  } catch {
    reportObserverError()
    return false
  }
}

export function setUiReadinessHandoffObserver(next: UiReadinessHandoffObserver): () => void {
  const previous = observer
  observer = next
  return () => {
    if (observer === next) observer = previous
  }
}

function observe<T>(callback: (current: UiReadinessHandoffObserver) => T): T | undefined {
  if (!observer || !readinessEnabled()) return undefined
  try {
    return callback(observer)
  } catch {
    reportObserverError()
    return undefined
  }
}

function boundedString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0 && value.length <= 128
}

export function readUiReadinessHandoff(value: unknown): UiReadinessHandoff | undefined {
  if (!readinessEnabled()) return undefined
  try {
    return parseHandoff(value)
  } catch {
    reportObserverError()
    return undefined
  }
}

function parseHandoff(value: unknown): UiReadinessHandoff | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined
  const seed = value as Record<string, unknown>
  if (
    seed.version !== 1 ||
    !boundedString(seed.clientOpenId) ||
    !boundedString(seed.clockId) ||
    !boundedString(seed.surface) ||
    !boundedString(seed.entryKind) ||
    !boundedString(seed.repositoryScope) ||
    typeof seed.observedAtMs !== 'number' ||
    !Number.isFinite(seed.observedAtMs) ||
    seed.observedAtMs < 0 ||
    typeof seed.monotonicMs !== 'number' ||
    !Number.isFinite(seed.monotonicMs) ||
    seed.monotonicMs < 0
  ) {
    return undefined
  }
  return {
    version: 1,
    clientOpenId: seed.clientOpenId,
    clockId: seed.clockId,
    observedAtMs: seed.observedAtMs,
    monotonicMs: seed.monotonicMs,
    surface: seed.surface,
    entryKind: seed.entryKind,
    repositoryScope: seed.repositoryScope,
  }
}

export function beginUiReadinessHandoff(source: string): UiReadinessHandoff | undefined {
  return observe(current => readUiReadinessHandoff(current.opened(source)))
}

export function claimUiReadinessHandoff(value: unknown): void {
  const seed = readUiReadinessHandoff(value)
  if (seed) observe(current => current.claimed(seed))
}

export function failUiReadinessHandoff(seed: UiReadinessHandoff): void {
  observe(current => current.failed(seed))
}
